// frontend/src/components/sidebar/Sidebar.jsx
import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Home,
  MessageCircle,
  TestTube,
  History,
  BarChart3,
  Settings,
  User,
  LogOut,
  Bot,
  Zap,
  ChevronDown,
  ChevronRight,
  Building,
  Clock,
  FileText,
  Plus
} from 'lucide-react'


import NavigationItem from './NavigationItem'
import UserProfile from './UserProfile'
import { useAuth } from '../../hooks/useAuth'
import { useBrowserSession } from '../../hooks/useBrowserSession'

const Sidebar = ({ onClose }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { activeSessions } = useBrowserSession()

  // Modern navigation items matching your actual routes
  const navigationItems = [
    {
      label: 'Home',
      icon: Home,
      path: '/',
    },
    {
      label: 'Conversational Mode',
      icon: MessageCircle,
      path: '/conversational',
    },
    {
      label: 'Automation Mode',
      icon: TestTube,
      path: '/automation',
    },
    {
      label: 'Analytics',
      icon: BarChart3,
      path: '/analytics',
    },
    {
      label: 'Figma Comparison',
      icon: Zap,
      path: '/figma-comparison',
    },
    {
      label: 'Test History',
      icon: History,
      path: '/test-history',
    },
    {
      label: 'Settings',
      icon: Settings,
      path: '/settings',
    }
  ]

  // Projects section (optional, can be expanded)
  const projects = [
    { label: 'Web Application', icon: Building, path: '/projects/web' },
    { label: 'Mobile App', icon: Clock, path: '/projects/mobile' },
    { label: 'API Service', icon: FileText, path: '/projects/api' }
  ]

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
      onClose?.()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleNavigation = (path) => {
    navigate(path)
    onClose?.()
  }

  return (
    <aside className="h-full bg-white/80 backdrop-blur-2xl border-r border-gray-200 text-gray-800 flex flex-col shadow-2xl min-w-[220px] max-w-xs">
      {/* Brand/Header */}
      <div className="p-6 border-b border-gray-100 flex items-center space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
          <Bot className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">TestCrew</h1>
          <p className="text-xs text-gray-500 font-semibold">AI QA Platform</p>
        </div>
      </div>

      {/* Company Section */}
      {/* <div className="p-4 border-b border-gray-100">
        <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
          <div className="w-6 h-6 bg-gray-600 rounded flex items-center justify-center">
            <Building className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Acme Inc</p>
            <p className="text-xs text-gray-500">Enterprise</p>
          </div>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </div>
      </div> */}

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto scrollbar-thin">
        <div className="space-y-1">
          {navigationItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.07 }}
            >
              <NavigationItem
                icon={item.icon}
                label={item.label}
                isActive={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
              />
            </motion.div>
          ))}
        </div>

        {/* Projects Section */}
        <div className="pt-6">
          <div className="px-3 mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Projects
            </h3>
          </div>
          <div className="space-y-1">
            {projects.map((project, index) => (
              <motion.button
                key={project.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + (index * 0.1) }}
                onClick={() => handleNavigation(project.path)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-left ${
                  location.pathname === project.path
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <project.icon className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium">{project.label}</span>
              </motion.button>
            ))}
            {/* Add New Project */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              onClick={() => handleNavigation('/projects/new')}
              className="w-full flex items-center space-x-3 px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-left"
            >
              <Plus className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium">Add New Project</span>
            </motion.button>
          </div>
        </div>
      </nav>

      {/* User Profile and Logout at Bottom */}
      <div className="p-4 border-t border-gray-100 bg-white/80 backdrop-blur-xl">
        <UserProfile user={user} />
        <button
          onClick={handleLogout}
          className="mt-4 w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-left text-red-600 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-200"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </aside>
  )
}

export default Sidebar